<template>
  <div :class="['search-suggestion', { 'result-page': isResult }]">
    <searched-text-layout
      v-if="searchObject.searchResult && hasEnteredValues"
    />
    <div ref="boxMap" style="display: none" />
    <template v-if="showSuggestions">
      <template v-if="loadingSuggestions">
        <v-progress-circular
          indeterminate
          color="grey darken-1"
          size="15"
          width="2"
        />
        <div class="small mt-1">
          Loading suggestions...
        </div>
      </template>
      <v-data-table
        v-else
        class="search-box-table"
        :headers="suggestionHeaders"
        :items="suggestions"
        item
        hide-actions
        hide-headers
      >
        <template v-slot:items="props">
          <template v-if="props.item.value.length > 0">
            <td class="suggestion-group">
              {{ props.item.text }}
            </td>
            <td class="suggestion-container">
              <div
                v-for="suggestion in props.item.value"
                :key="suggestion.placeId"
                class="suggestion-value"
              >
                <span @click.stop="addSuggestion(suggestion)">{{
                  suggestion.placeDesc
                }}</span>
              </div>
            </td>
          </template>
        </template>
      </v-data-table>
    </template>
  </div>
</template>

<script>
import { mapState, mapMutations } from 'vuex'
import { googleMapInit } from '../../services/googleMapService'
import SearchedTextLayout from '../common/search/SearchedTextLayout.vue'
import {
  DEFAULT_SUGGESTIONS,
  SUGGESTED_PARAMS,
  STREET_FULL_SUFFIXES
} from '../common/searchPropertyConst'
import { mlsCondition } from '../../utils/propertyUtils'
import { isEmpty, hasNonEmptyKey } from '../../utils/generalUtils'

export default {
  components: { SearchedTextLayout },

  props: {
    enteredInput: {
      type: Array,
      required: true
    },
    isResult: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      autoService: null,
      placeService: null,
      loadingSuggestions: true,
      suggestions: JSON.parse(JSON.stringify(DEFAULT_SUGGESTIONS)),
      suggestionHeaders: [
        { text: '', value: '', sortable: false },
        { text: '', value: '', sortable: false }
      ]
    }
  },

  computed: {
    ...mapState('common', [
      'addressInputValue',
      'multipleRequestedObject',
      'searchTextParams',
      'searchObject'
    ]),

    showSuggestions() {
      return !isEmpty(this.enteredInput)
    },

    hasEnteredValues() {
      return hasNonEmptyKey(this.searchTextParams)
    }
  },

  watch: {
    enteredInput: {
      handler: function(newValue) {
        if (this.autoService) {
          this.suggestionListener(newValue[newValue.length - 1])
        }
      },
      deep: true
    }
  },

  async mounted() {
    try {
      const google = await googleMapInit()
      this.autoService = new google.maps.places.AutocompleteService()
      const map = new google.maps.Map(this.$refs.boxMap)
      this.placeService = new google.maps.places.PlacesService(map)

      if (!isEmpty(this.enteredInput)) {
        this.suggestionListener(
          this.enteredInput[this.enteredInput.length - 1]
        )
      }
    } catch (error) {
      console.log(error)
    }
  },

  methods: {
    ...mapMutations('common', {
      setMultipleRequestedObject: 'SET_MULTIPLE_REQUESTED_OBJECT'
    }),

    // Add google distributed params to requested object
    addToRequestedParams(rObject, value, type) {
      if (!rObject[type].includes(value)) {
        rObject[type].push(value)
      }
      return rObject
    },

    suggestionListener(value) {
      const request = {
        input: value,
        componentRestrictions: { country: 'us' }
      }
      this.autoService.getPlacePredictions(request, this.predictionCallback)
    },

    predictionCallback(predictions, status) {
      this.suggestions = JSON.parse(JSON.stringify(DEFAULT_SUGGESTIONS))
      const inputValue = this.enteredInput[this.enteredInput.length - 1]
      if (status === 'OK' && predictions.length > 0) {
        predictions.map(prediction => {
          const predictionType = prediction.types
          if (
            predictionType.some(type => SUGGESTED_PARAMS.indexOf(type) >= 0)
          ) {
            for (let i = 0; i < SUGGESTED_PARAMS.length; i++) {
              if (predictionType.includes(SUGGESTED_PARAMS[i])) {
                this.suggestions[i].value.push({
                  placeId: prediction.place_id,
                  placeDesc: i === 2 ? inputValue : prediction.description
                })
              }
            }
          } else {
            this.suggestions[SUGGESTED_PARAMS.length + 1].text = 'Address'
            this.suggestions[SUGGESTED_PARAMS.length + 1].value.push({
              placeId: prediction.place_id,
              placeDesc: prediction.description
            })
          }
        })
      } else {
        if (mlsCondition(inputValue)) {
          this.suggestions[SUGGESTED_PARAMS.length].value.push({
            placeDesc: inputValue
          })
        }
        this.suggestions[SUGGESTED_PARAMS.length + 1].value.push({
          placeDesc: this.enteredInput[this.enteredInput.length - 1]
        })
      }
      this.loadingSuggestions = false
    },

    addSuggestion(suggestion) {
      this.$emit('add-suggestion-entry', suggestion)
      this.getPlaceServiceDetails(suggestion, true, false)
    },

    getMlsStreetName(addressString) {
      let suffixIndex = -1
      let addressArray = addressString.split(' ')
      suffixIndex = addressArray.findIndex(item =>
        STREET_FULL_SUFFIXES.includes(item)
      )
      if (suffixIndex > -1) {
        addressArray = addressArray.splice(0, suffixIndex)
        return addressArray.join(' ')
      }
      return addressString
    },

    getPlaceServiceDetails(suggestion, multiple = false, search = true) {
      let placeObj = {
        City: [],
        PostalCode: [],
        StreetNumber: [],
        StreetName: [],
        MLSNumber: [],
        Keywords: ''
      }

      let requestedObject = { ...this.multipleRequestedObject }

      if (this.placeService && suggestion.placeId) {
        const request = { placeId: suggestion.placeId }
        this.placeService.getDetails(request, (place, status) => {
          if (status === 'OK') {
            const addressComps = place.address_components
            if (addressComps) {
              addressComps.forEach(address => {
                const addressType = address.types
                if (addressType.includes('street_number')) {
                  multiple
                    ? (requestedObject = this.addToRequestedParams(
                      requestedObject,
                      address.long_name,
                      'StreetNumber'
                    ))
                    : placeObj.StreetNumber.push(address.long_name)
                }
                if (addressType.includes('route')) {
                  let streetName = address.long_name
                  streetName = this.getMlsStreetName(streetName)
                  multiple
                    ? (requestedObject = this.addToRequestedParams(
                      requestedObject,
                      streetName,
                      'StreetName'
                    ))
                    : placeObj.StreetName.push(streetName)
                }
                if (addressType.includes('locality')) {
                  if (multiple) {
                    if (isEmpty(requestedObject.PostalCode)) {
                      requestedObject = this.addToRequestedParams(
                        requestedObject,
                        address.long_name,
                        'City'
                      )
                    }
                  } else {
                    if (isEmpty(placeObj.PostalCode)) {
                      placeObj.City.push(address.long_name)
                    }
                  }
                }
                if (addressType.includes('postal_code')) {
                  if (multiple) {
                    if (isEmpty(requestedObject.City)) {
                      requestedObject = this.addToRequestedParams(
                        requestedObject,
                        address.long_name,
                        'PostalCode'
                      )
                    }
                  } else {
                    if (isEmpty(placeObj.City)) {
                      placeObj.PostalCode.push(address.long_name)
                    }
                  }
                }
              })
            } else {
              multiple
                ? (requestedObject.Keywords = place.name)
                : (placeObj.Keywords = place.name)
            }
          }

          if (multiple) {
            this.$emit('set-requested-query', {
              obj: requestedObject,
              desc: this.addressInputValue.text || '',
              search
            })
          } else {
            this.$emit('set-requested-query', {
              obj: placeObj,
              desc: this.addressInputValuetext || '',
              search
            })
          }
        })
      } else {
        if (multiple) {
          if (mlsCondition(suggestion.placeDesc)) {
            requestedObject = this.addToRequestedParams(
              requestedObject,
              suggestion.placeDesc,
              'MLSNumber'
            )
            this.$emit('set-requested-query', {
              obj: requestedObject,
              desc: this.addressInputValue.text || '',
              search
            })
          } else {
            this.$emit('set-requested-query', {
              obj: {
                ...requestedObject,
                Keywords: this.addressInputValue.text
              },
              desc: this.addressInputValue.text || '',
              search
            })
          }
        } else {
          this.$emit('set-requested-query', {
            obj: { ...placeObj, Keywords: this.addressInputValue.text },
            desc: this.addressInputValue.text || '',
            search
          })
        }
      }
      this.setMultipleRequestedObject(requestedObject)
    }
  }
}
</script>

<style lang="sass" scoped>
.search-suggestion
  position: absolute
  margin-top: -2rem
  z-index: 99
  max-height: 40rem
  overflow: auto
  background: $hwayzWhite
  min-width: 200%
  webkit-box-shadow: 0.1rem 0.1rem 0.5rem 0px rgba($black, 0.2)
  box-shadow: 0.1rem 0.1rem 0.5rem 0px rgba($black, 0.2)
  @media only screen and (max-width: 600px)
    min-width: 100%
    width: 100%

.search-box-table
  tr
    padding: 1rem
    td
      padding-top: 0.5rem !important
      padding-bottom: 0.5rem !important
  .suggestion-group
    text-align: left
    padding-left: 1rem
    width: 25%
    font-size: 1.5rem
  .suggestion-container
    text-align: left
    padding-left: 1rem
    width: 75%
    .suggestion-value
      margin: 0.5rem
      display: flex
      align-items: center
      span
        display: inline-block
        width: 100%
        padding: 0.3rem 1rem 0.3rem 0.3rem
        cursor: pointer
        transition: all 0.3s ease-in-out
        &:hover
          background-color: $hwayzShadow
      .suggestion-plus
        cursor: pointer
        border-radius: 50%
        width: 25px
        height: 25px
        &:hover
          background-color: $hwayzShadow

.search-suggestion.result-page
  margin-top: 0
  text-transform: none
  min-width: 300%
  .suggestion-group
    font-size: 1.4rem
  .suggestion-value
    padding: 0.2rem

.no-suggestion
  font-size: 1.2rem
  color: $hwayzBlack
</style>
